import {
  forwardRef,
  LegacyRef,
  MouseEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  CloseCircleBoldIcon,
  EditPencilThinIcon,
  EyeIcon,
  EyeSlashIcon,
  TickCircleBoldIcon,
} from "components/icons";

import { useTranslationContext } from "context/TranslationContext";
import {
  COMMON_ALLOWED_KEY_CODES,
  MINUS_KEY_CODES,
  NUMPAD_DIGIT_CODES,
  TOP_ROW_DIGIT_CODES,
} from "helpers/keyboardKeys";
import cn from "lib/cn";

import styles from "./Input.module.css";

export interface InputProps extends React.ComponentPropsWithRef<"input"> {
  error?: string;
  successMessage?: string;
  className?: string;
  label?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  loading?: boolean;
  optional?: boolean;
  fieldClassName?: string;
  containerClassName?: string;
  containerRef?: LegacyRef<HTMLDivElement>;
  pencil?: boolean;
  eye?: boolean;
  withEyeDefaultHiddenState?: "hidden" | "visible";
  inputStyle?: string;
  rightIconStyle?: string;
  inputClassName?: string;
  rightIconClassName?: string;
  leftIconClassName?: string;
  labelClassName?: string;
  digitOnly?: boolean;
  labelTextClassName?: string;
  allowMinus?: boolean;
  rightLabel?: string;
  childLabel?: string;
  hideErrorMessage?: boolean;
  onClickLeftIcon?: (e: MouseEvent<HTMLDivElement>) => void;
  onClickRightIcon?: (e: MouseEvent<HTMLDivElement>) => void;
  onFocusEl?: () => void;
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      id,
      leftIcon,
      rightIcon,
      error,
      label,
      required,
      className,
      disabled,
      containerClassName,
      containerRef,
      loading,
      fieldClassName,
      pencil,
      eye,
      optional,
      withEyeDefaultHiddenState = "visible",
      type = "text",
      inputClassName,
      rightIconClassName,
      successMessage,
      leftIconClassName,
      labelClassName,
      labelTextClassName,
      digitOnly,
      allowMinus,
      rightLabel,
      childLabel,
      hideErrorMessage = false,
      onFocusEl,
      onClickLeftIcon,
      onClickRightIcon,
      ...rest
    },
    ref
  ) => {
    const { t } = useTranslationContext();
    const internalRef = useRef<HTMLInputElement>(null);
    const [disabledState, setDisabledState] = useState(disabled);
    const [focus, setFocus] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [isHidden, setIsHidden] = useState(
      withEyeDefaultHiddenState === "hidden"
    );

    const rightIconValue = useMemo(() => {
      if (pencil && !isEdit && !disabled) {
        return (
          <EditPencilThinIcon
            className="text-neutral-gray-200"
            width={14}
            height={14}
          />
        );
      }
      if (eye) {
        if (isHidden)
          return (
            <EyeSlashIcon className="text-gray-500" width={18} height={18} />
          );
        return <EyeIcon className="text-gray-500" width={18} height={18} />;
      }
      return rightIcon;
    }, [pencil, isEdit, disabled, eye, rightIcon, isHidden]);

    const handleOnFocus = (e?: React.FocusEvent<HTMLInputElement>) => {
      setFocus(true);
      if (rest.onFocus && e) rest.onFocus(e);
    };

    const handleOnBlur = (e?: React.FocusEvent<HTMLInputElement>) => {
      setFocus(false);
      if (rest.onBlur && e) rest.onBlur(e);
    };

    const handleClickRightIcon = (e: MouseEvent<HTMLDivElement>) => {
      e?.stopPropagation();

      if (pencil) {
        setTimeout(() => {
          internalRef.current?.focus();
          onFocusEl && onFocusEl();
        }, 300);
        setIsEdit(true);
      }
      if (eye) {
        setIsHidden((prev) => !prev);
      }
      onClickRightIcon && onClickRightIcon(e);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLSpanElement>) => {
      if (rest?.onKeyDown) {
        rest.onKeyDown(e as unknown as React.KeyboardEvent<HTMLInputElement>);
      }

      if (!digitOnly) return;

      if (e.metaKey || e.ctrlKey) {
        return;
      }

      const isTopRowDigit = TOP_ROW_DIGIT_CODES.includes(e.code);
      const isNumpadDigit = NUMPAD_DIGIT_CODES.includes(e.code);
      const isUtilityKey = COMMON_ALLOWED_KEY_CODES.includes(e.code);
      const isMinusKey = allowMinus && MINUS_KEY_CODES.includes(e.code);

      if (!isTopRowDigit && !isNumpadDigit && !isUtilityKey && !isMinusKey) {
        e.preventDefault();
      }
    };

    useEffect(() => {
      if (isEdit) {
        setDisabledState(false);
        handleOnFocus();
      } else handleOnBlur();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isEdit]);

    useEffect(() => {
      if (pencil) {
        setDisabledState(true);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
      setDisabledState(disabled);
    }, [disabled]);

    return (
      <div
        ref={containerRef}
        id={id}
        data-testid={id}
        className={cn("w-full", className)}
      >
        <div className="flex items-center justify-between gap-2">
          <div className={label ? "mb-1.5" : ""}>
            {!!label && (
              <div className={cn("text-base", labelClassName)}>
                <span
                  className={cn("text-gray-500 text-base", labelTextClassName)}
                >
                  {label}
                </span>
                {required && <span className="text-red-500"> *</span>}
                {optional && (
                  <span className="text-gray-500 text-base">
                    {` `}({t("optional")})
                  </span>
                )}
              </div>
            )}

            {!!childLabel && (
              <div className="text-sm text-gray-900">{childLabel}</div>
            )}
          </div>

          {!!rightLabel && (
            <div className="text-sm text-gray-900">{rightLabel}</div>
          )}
        </div>
        <div
          className={cn(
            "flex justify-between items-center relative rounded-lg",
            !disabled &&
              "hover:shadow-[0px_0px_1px_1px_rgb(0,122,245,0.4)] hover:ring-primary-500",
            containerClassName
          )}
        >
          {!!leftIcon && (
            <div
              data-testid={id ? `leftIcon-${id}` : undefined}
              className={cn("pl-3 cursor-pointer absolute", leftIconClassName)}
              onClick={onClickLeftIcon}
            >
              {leftIcon}
            </div>
          )}
          <div
            data-testid={id ? `input-container-${id}` : undefined}
            className={cn([
              "w-full flex justify-between items-center gap-2 border border-gray-200 rounded-lg  px-3 py-2",
              disabled && "bg-gray-200",
              error && ["border border-red-500"],
              focus &&
                "ring-primary-500 border-primary-500 shadow-[0px_0px_2px_0px_rgb(0,122,245)]",
              "overflow-hidden",
              fieldClassName,
            ])}
          >
            <input
              ref={ref ? ref : internalRef}
              id={id ? `input-${id}` : undefined}
              data-testid={id ? `input-${id}` : undefined}
              className={cn([
                "focus:outline-none w-full bg-transparent placeholder-gray-200 text-night",
                leftIcon && "pl-[40px]",
                rightIconValue && "pr-[40px]",
                inputClassName,
              ])}
              min={type === "number" && !allowMinus ? 0 : undefined}
              autoComplete="off"
              type={isHidden ? "password" : type}
              disabled={disabledState || disabled}
              {...rest}
              onKeyDown={handleKeyDown}
              onBlur={handleOnBlur}
              onFocus={handleOnFocus}
            />

            {loading && (
              <div className="flex items-center">
                <div className={cn(styles.loader)} />
              </div>
            )}
          </div>
          {!!rightIconValue && (
            <div
              data-testid={id ? `rightIcon-${id}` : undefined}
              className={cn(
                "pr-3 right-2 cursor-pointer absolute",
                rightIconClassName
              )}
              onClick={handleClickRightIcon}
            >
              {rightIconValue}
            </div>
          )}
        </div>
        {!!error && !hideErrorMessage && (
          <div
            data-testid={id ? `error-input-${id}` : undefined}
            className={cn(
              "text-[0.875rem] text-red-500 ml-[0.1rem] items-center flex mt-3"
            )}
          >
            <CloseCircleBoldIcon className="mr-1 shrink-0" /> {error}
          </div>
        )}
        {!!successMessage && !error && (
          <div
            data-testid={id ? `error-input-${id}` : undefined}
            className={cn(
              "text-[0.875rem] text-primary-500 ml-[0.1rem] items-center flex mt-3"
            )}
          >
            <TickCircleBoldIcon className="mr-1 shrink-0" /> {successMessage}
          </div>
        )}
      </div>
    );
  }
);

export default Input;
